require("./threepl-services-view.scss");

import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal} from "@angular/core";
import {BreakpointObserver} from "@angular/cdk/layout";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {finalize, map} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {NgxPermissionsService} from "ngx-permissions";
import * as moment from "moment";
import {BzMenuItem, BzTab} from "@bringoz/ui";
import {bzIconChevronDown, bzIconExport, bzIconExportXsl} from "@bringoz/ui/icons";
import {ThreePLServicesStore} from "../../state/threepl-services.store";
import {matchesServicesFilters} from "../../state/threepl-services-filter";
import {ThreePLServiceDetails} from "../../models/threepl-service-details.model";
import {
  ThreePLServicesFilters, ThreePLServicesListQuery, ThreePLServicesSettings, ThreePLServicesSortName, ThreePLServicesSortValue,
  ThreePLServicesTableLayout, ThreePLServicesTotals, nextSortValue,
} from "../../models/threepl-services-list.model";
import {ThreePLServiceHttpService, ThreePLServicesExportPayload} from "../../services/threepl-service-http.service";
import {ThreePLServiceActionsService} from "../../services/threepl-service-actions.service";
import {ThreePLServicePush, ThreePLServicesChannelService} from "../../services/threepl-services-channel.service";
import {ServiceActionEvent} from "../threepl-service-actions/threepl-service-actions.component";
import {BringozDateRange} from "../../../../commons/angular/models/bringoz-date-range.model";
import {DateRangeTemplateType} from "../../../../commons/types/date-range-template.type";
import {ExportType} from "../../../../commons/types/export.type";
import {AccountType} from "../../../../account/types/account.type";
import {ViewSettingsModel} from "../../../../commons/models/view-settings.model";
import {EXPORT_CSV_SIZE_LIMIT, EXPORT_EXCEL_SIZE_LIMIT} from "../../../../commons/angular/constants/commons";
import {ILogService} from "angular";
import {
  ActionReasonsDialogsServiceService,
} from "../../../../action-reasons/services/action-reasons-dialogs-service.service";
import {UiRouterStateService} from "../../../../commons/angular/models/ui-router-state.model";
import {CommonsDialogService} from "../../../../commons/services/commons-dialog.service";
import {ConfirmDialogService} from "../../../../commons/services/confirm-dialog.service";
import {DateRangeContextService} from "../../../../commons/services/date-range-context.service";
import {DateRangeTimeFiltersService} from "../../../../commons/services/date-range-time-filters.service";
import {SplitService} from "../../../../commons/services/split-service.service";
import {ViewFilterSettingsService} from "../../../../commons/services/view-filter-settings.service";
import {DeliveriesAlertsService} from "../../../../deliveries/services/deliveries-alerts.service";
import {FulfillmentDialogService} from "../../../../fulfillment/services/fulfillment-dialogs-service.service";
import {FulfillmentService} from "../../../../fulfillment/services/fulfillment-service.service";
import {MEDIA_GT_SM} from "../../../../commons/angular/constants/media-breakpoints.constant";
import {STATE_URL_ONLY_OPTIONS} from "../../constants/threepl.constant";
import {
  THREE_PL_SERVICES_EXPORT_FIELDS, THREE_PL_SERVICES_EXPORT_LOCATION_FIELDS, THREE_PL_SERVICES_GROUP_MENU,
  THREE_PL_SERVICES_GROUP_TABS, THREE_PL_SERVICES_INCLUDE_FILTERS, THREE_PL_SERVICES_PAGE_SIZE,
} from "../../constants/threepl-services.constant";

@Component({
  selector: "app-threepl-services-view",
  templateUrl: "./threepl-services-view.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ThreePLServicesStore],
})
export class ThreeplServicesViewComponent implements OnInit {
  readonly groupParam = input<string>(undefined, {alias: "group"});
  readonly from = input<number>();
  readonly to = input<number>();
  readonly depotIds = input<string[]>();
  readonly accountIds = input<string[]>();
  readonly branchIds = input<string[]>();
  readonly supplyAccountIds = input<string[]>();
  readonly serviceAreaIds = input<string[]>();
  readonly providerTypes = input<string[]>();
  readonly serviceTypes = input<string[]>();
  readonly programTypes = input<string[]>();
  readonly dateRangeTemplateType = input<DateRangeTemplateType>();
  readonly selectedQuickFilterIdParam = input<string>(undefined, {alias: "selectedQuickFilterId"});

  readonly store = inject(ThreePLServicesStore);
  readonly vm = toSignal(this.store.vm$, {requireSync: true});

  private readonly http = inject(ThreePLServiceHttpService);
  private readonly actions = inject(ThreePLServiceActionsService);
  private readonly channel = inject(ThreePLServicesChannelService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly permissions = inject(NgxPermissionsService);
  private readonly split = inject<SplitService>("splitService" as never);
  private readonly state = inject<UiRouterStateService>("$state" as never);
  private readonly dateRangeTimeFiltersService = inject<DateRangeTimeFiltersService>("dateRangeTimeFiltersService" as never);
  private readonly viewFilterSettingsService = inject<ViewFilterSettingsService>("viewFilterSettingsService" as never);
  private readonly dateRangeContextService = inject<DateRangeContextService>("dateRangeContextService" as never);
  private readonly deliveriesAlertsService = inject<DeliveriesAlertsService>("deliveriesAlertsService" as never);
  private readonly confirmDialogService = inject<ConfirmDialogService>("confirmDialogService" as never);
  private readonly commonsDialogService = inject<CommonsDialogService>("commonsDialogService" as never);
  private readonly actionReasonsDialogsService = inject<ActionReasonsDialogsServiceService>("actionReasonsDialogsService" as never);
  private readonly fulfillmentService = inject<FulfillmentService>("fulfillmentService" as never);
  private readonly fulfillmentDialogService = inject<FulfillmentDialogService>("fulfillmentDialogService" as never);
  private readonly log = inject<ILogService>("$log" as never);

  readonly includeFilters = THREE_PL_SERVICES_INCLUDE_FILTERS;
  readonly queryMap = {supplierType: AccountType.ThreePLSupply};
  readonly pageName = this.state.current.name;
  readonly hasDeliveriesListPermission = this.hasPermission("services.list");
  readonly has3plListPermission = this.hasPermission("threePL.list");
  readonly icons = {exportXsl: bzIconExportXsl, export: bzIconExport, chevron: bzIconChevronDown};
  readonly exportTypes = ExportType;
  readonly servicesNavTabs: BzTab[] = [
    {id: "internal", label: this.translate.instant("Internal")},
    {id: "threepl", label: this.translate.instant("External")},
  ];

  customTemplates: BringozDateRange[] = [];
  rangeLimit: {min: number | undefined; max: number | undefined} = {min: undefined, max: undefined};

  readonly group = signal<string | undefined>(undefined);
  readonly range = signal<BringozDateRange>({dateStart: undefined, dateEnd: undefined});
  readonly filters = signal<ThreePLServicesFilters>({});
  readonly searchTerm = signal<string | undefined>(undefined);
  readonly settings = signal<ThreePLServicesSettings>({});
  readonly selectedQuickFilterId = signal<string | undefined>(undefined);
  readonly sortValue = signal<ThreePLServicesSortValue | undefined>(undefined);
  readonly totals = signal<ThreePLServicesTotals | undefined>(undefined);
  readonly totalsLoading = signal(false);
  readonly selectedServices = signal<ThreePLServiceDetails[]>([]);
  readonly isTimeSelectionDisabled = signal(false);

  private readonly isGtSm = toSignal(inject(BreakpointObserver).observe(MEDIA_GT_SM).pipe(map((s) => s.matches)), {initialValue: true});
  readonly isMobile = computed(() => !this.isGtSm());

  readonly selectedServiceIds = computed(() => this.selectedServices().map((s) => s.id));

  readonly selectedGroupTab = computed(() => this.group() || "TOTAL");

  readonly groupTabs = computed<BzTab[]>(() => {
    const loading = this.totalsLoading();
    const totals = this.totals();
    return THREE_PL_SERVICES_GROUP_TABS.map((tab) => ({
      id: tab.id,
      label: `${this.translate.instant(tab.label)}\u2002 ${loading ? "..." : totals?.[tab.key] ?? ""}`,
      disabled: loading,
    }));
  });

  readonly groupMenuItems = computed<BzMenuItem[]>(() => {
    const totals = this.totals();
    return THREE_PL_SERVICES_GROUP_MENU.map((item) => ({
      id: item.id,
      label: `${this.translate.instant(item.label)} ( ${item.key === "total" ? totals?.total ?? "" : totals?.[item.key] || 0} )`,
    }));
  });

  readonly groupMenuCount = computed(() => this.totals()?.["group"]);

  readonly listEmpty = computed(() => !this.vm().items.length);

  lastExportTriggerClick: MouseEvent | undefined;

  ngOnInit(): void {
    if (!this.has3plListPermission) {
      this.state.go("deliveries.table");
    }

    const blockingLimit = this.split.getBlockingSearchDatesRangeLimit();
    const from = this.from();
    const to = this.to();
    this.group.set(this.groupParam());
    this.selectedQuickFilterId.set(this.selectedQuickFilterIdParam());
    this.customTemplates = this.dateRangeTimeFiltersService.getDeliveriesCustomTemplates();
    this.rangeLimit = {
      min: !blockingLimit ? undefined : moment().subtract(blockingLimit, "day").startOf("day").valueOf(),
      max: undefined,
    };
    this.filters.set({
      dateRangeTemplateType: this.dateRangeTemplateType(),
      accountIds: this.accountIds() || [],
      branchIds: this.branchIds() || [],
      supplyAccountIds: this.supplyAccountIds() || [],
      serviceAreaIds: this.serviceAreaIds() || [],
      depotIds: this.depotIds() || [],
      providerTypes: this.providerTypes() || [],
      serviceTypes: this.serviceTypes() || [],
      programTypes: this.programTypes() || [],
    });
    this.range.set(from && to ? {dateStart: from, dateEnd: to} : this.dateRangeTimeFiltersService.today());
    this.settings.set(this.split.getSite3plServiceSettings());
    this.isTimeSelectionDisabled.set(!isSameDay(from, to));

    this.store.load(this.query());
    this.loadTotals();
    this.listenForPushes();
  }

  onServicesNavChange(tabId: string): void {
    this.state.go(tabId === "internal" ? "deliveries.table" : "threePLServices.table");
  }

  changeGroup(group: string): void {
    this.group.set(group);
    this.reloadServices(true);
    this.setStateParams();
  }

  onGroupMenuSelected(item: BzMenuItem): void {
    this.changeGroup(item.id);
  }

  submitQuery(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
    this.reloadServices(false);
  }

  onQuickFilterSelect(quickFilter: ViewSettingsModel | undefined): void {
    if (quickFilter) {
      this.onFiltersChange(quickFilter.settings as ThreePLServicesFilters, quickFilter);
    } else {
      const cleared = Object.fromEntries(Object.entries(this.filters()).map(([key, value]) => [key, Array.isArray(value) ? [] : undefined]));
      this.onFiltersChange(cleared as ThreePLServicesFilters);
    }
  }

  onViewSettingsChange(settings: ThreePLServicesSettings): void {
    this.settings.set(settings);
    this.split.setSite3plServiceSettings(settings);
    this.deliveriesAlertsService.viewSettingsSuccess();
  }

  onTableLayoutChange(layout: ThreePLServicesTableLayout): void {
    this.settings.update((settings) => ({...settings, ...layout}));
    this.split.setSite3plServiceSettings(this.settings());
  }

  onFiltersChange(filters: ThreePLServicesFilters, selectedQuickFilter?: ViewSettingsModel): void {
    this.viewFilterSettingsService.setActiveQuickFilter(selectedQuickFilter, this.pageName);
    this.selectedQuickFilterId.set(selectedQuickFilter?.id);
    this.filters.set({...filters});

    if (filters.dateRangeTemplateType) {
      this.range.set(this.dateRangeTimeFiltersService.getDateRangeFilterByType(filters.dateRangeTemplateType));
    }

    this.reloadServices(true);
    this.setStateParams();
  }

  onRangeChanged(range: BringozDateRange): void {
    if (range) {
      this.isTimeSelectionDisabled.set(!isSameDay(range.dateStart, range.dateEnd));
    }

    if (this.selectedQuickFilterId() && range?.type !== this.filters().dateRangeTemplateType) {
      this.selectedQuickFilterId.set(undefined);
    }

    const templateType = this.dateRangeTimeFiltersService.getDateRangeFilterByType(range.type as DateRangeTemplateType)?.type as DateRangeTemplateType;
    this.filters.update((filters) => ({...filters, dateRangeTemplateType: templateType}));
    this.viewFilterSettingsService.setDateRangeTemplateType(templateType || undefined);
    this.dateRangeContextService.setRange(range.dateStart, range.dateEnd);

    this.changeRange(range);
  }

  onTimeSelect(range: BringozDateRange): void {
    this.changeRange(range);
  }

  onToggleService(service: ThreePLServiceDetails): void {
    this.selectedServices.update((selected) => (selected.some((s) => s.id === service.id)
      ? selected.filter((s) => s.id !== service.id)
      : [...selected, service]));
  }

  onUnselectAll(): void {
    this.selectedServices.set([]);
  }

  onSortTypeChange(sortName: ThreePLServicesSortName): void {
    this.sortValue.set(nextSortValue(this.sortValue(), sortName));
    this.reloadServices(false);
  }

  onAddNoteToService({event, service}: ServiceActionEvent): void {
    this.addNote(event, [service.id]);
  }

  onAddNoteToSelectedServices(event: MouseEvent): void {
    this.addNote(event, this.selectedServiceIds());
  }

  onCancelService({event, service}: ServiceActionEvent): void {
    this.cancel(event, [service.id], "Cancel Service");
  }

  onCancelSelectedServices(): void {
    this.cancel(undefined, this.selectedServiceIds(), "Cancel Services");
  }

  onMoveToInternal({event, service}: ServiceActionEvent): void {
    const fulfillmentId = service.fulfillmentStepInfoList?.[0]?.fulfillmentId;
    if (!fulfillmentId) {
      return;
    }
    Promise.resolve(this.fulfillmentService.getFulfillment(fulfillmentId, true))
      .then((fulfillment) => this.fulfillmentDialogService.dispatchStep(fulfillment, undefined, event))
      .catch((error: unknown) => {
        if (error) {
          this.log.debug("Failed to dispatch step", error);
        } else {
          this.log.info("Dialog Closed");
        }
      });
  }

  onExportTriggerClick(event: MouseEvent): void {
    this.lastExportTriggerClick = event;
  }

  onExport(event: MouseEvent | undefined, exportType: ExportType): void {
    const group = this.group();
    const totals = this.totals();
    if (!group || !totals) {
      return;
    }
    const matchedKey = Object.keys(totals).find((key) => key.toLowerCase() === group.toLowerCase());
    const limit = exportType === ExportType.Excel ? EXPORT_EXCEL_SIZE_LIMIT : EXPORT_CSV_SIZE_LIMIT;
    const extraText = matchedKey && totals[matchedKey] > limit ? "EXPORT_INFO_LIMIT" : undefined;

    Promise.resolve(this.confirmDialogService.confirmWithInfo("Export Deliveries to File", "Are you sure you want to export deliveries?",
      extraText, {x: limit}, event))
      .then(() => this.export(exportType))
      .catch(() => this.log.info("Dialog Closed"));
  }

  private export(exportType: ExportType): void {
    const group = this.group();
    const {dateStart, dateEnd} = this.range();
    const filters = this.filters();
    this.http.export({
      group: group.toLowerCase() === "total" ? undefined : group,
      from: dateStart,
      to: dateEnd,
      depotId: filters.depotIds,
      accountId: filters.accountIds,
      branchId: filters.branchIds,
      areaId: filters.serviceAreaIds,
      providerType: filters.providerTypes,
      supplyAccountId: filters.supplyAccountIds,
      serviceTypes: filters.serviceTypes,
      programType: filters.programTypes,
      searchTerm: this.searchTerm(),
      id: this.selectedServiceIds(),
      zoneId: this.split.getTimeZone(),
      type: exportType,
    }, this.exportPayload()).subscribe({
      next: () => this.deliveriesAlertsService.exportSuccess(),
      error: (error: unknown) => {
        this.deliveriesAlertsService.exportError();
        this.log.error("Failed to export deliveries", error);
      },
    });
  }

  private exportPayload(): ThreePLServicesExportPayload {
    const columns = this.settings().columns ?? {};
    const canListOrders = this.hasPermission("oms.orders.list");
    const fieldTranslationMap = Object.fromEntries(THREE_PL_SERVICES_EXPORT_FIELDS.map(([field, key]) =>
      [field, this.translate.instant(`EXPORT_SERVICES_COLUMNS.${key}`)]));
    const fieldsMap: Record<string, boolean> = {
      serviceId: true,
      externalId: true,
      orderId: canListOrders,
      omsOrderExternalId: canListOrders,
      branchNames: !!columns.operationalUnits,
      accountName: !!columns.account,
      carrier: !!columns.supplierAccount,
      ...Object.fromEntries(THREE_PL_SERVICES_EXPORT_LOCATION_FIELDS.map((field) => [field, !!columns.location])),
      taskApprovalStatus: !!columns.taskApprovalStatus,
      pickupContact: !!columns.contact,
      dropoffContact: !!columns.contact,
      programName: !!columns.programType,
      timeToDeliver: !!columns.timeToDeliver,
      distance: !!columns.distance,
      pickupStartTime: !!columns.timeWindow,
      pickupEndTime: !!columns.timeWindow,
      dropoffStartTime: !!columns.timeWindow,
      dropoffEndTime: !!columns.timeWindow,
      price: !!columns.price,
      notes: !!columns.notes,
      createdTime: !!columns.created,
      pickupPhone: !!columns.contact,
      dropoffPhone: !!columns.contact,
      serviceType: true,
      status: true,
      pickupServiceAreaIds: !!columns.serviceAreas,
      dropoffServiceAreaIds: !!columns.serviceAreas,
    };
    return {fieldTranslationMap, fieldsMap};
  }

  private addNote(event: MouseEvent | undefined, serviceIds: string[]): void {
    Promise.resolve(this.commonsDialogService.note(event, serviceIds))
      .then((note) => this.actions.addNote(serviceIds, note))
      .catch(() => undefined);
  }

  private cancel(event: MouseEvent | undefined, serviceIds: string[], header: string): void {
    Promise.resolve(this.actionReasonsDialogsService.createThreeplServiceCancelReason(serviceIds, event, header, true))
      .then((payload) => this.actions.cancelService(serviceIds, payload.cancelReason))
      .catch(() => undefined);
  }

  private changeRange(range: BringozDateRange | undefined): void {
    const current = this.range();
    if (range && (range.dateStart !== current.dateStart || range.dateEnd !== current.dateEnd)) {
      this.range.set(range);
      this.reloadServices(true);
      this.setStateParams();
    }
  }

  private reloadServices(loadTotals: boolean): void {
    this.store.load(this.query());
    if (loadTotals) {
      this.loadTotals();
    }
  }

  private loadTotals(): void {
    if (this.totalsLoading()) {
      return;
    }
    this.totalsLoading.set(true);
    const {dateStart, dateEnd} = this.range();
    const filters = this.filters();
    this.http.getTotals({
      from: dateStart,
      to: dateEnd,
      depotId: filters.depotIds,
      accountId: filters.accountIds,
      branchId: filters.branchIds,
      areaId: filters.serviceAreaIds,
      providerType: filters.providerTypes,
      supplyAccountId: filters.supplyAccountIds,
      serviceTypes: filters.serviceTypes,
      programType: filters.programTypes,
    }).pipe(finalize(() => this.totalsLoading.set(false)))
      .subscribe({
        next: (totals) => this.totals.set(totals),
        error: (error: unknown) => {
          if (error) {
            this.log.error("Failed to get totals @3plServicesView@loadTotals", error);
          }
        },
      });
  }

  private query(): ThreePLServicesListQuery {
    const {dateStart, dateEnd} = this.range();
    const filters = this.filters();
    const group = this.group();
    return {
      from: dateStart,
      to: dateEnd,
      statusGroup: group === "TOTAL" ? undefined : group,
      depotId: filters.depotIds,
      accountId: filters.accountIds,
      branchId: filters.branchIds,
      supplierAccountId: filters.supplyAccountIds,
      areaId: filters.serviceAreaIds,
      providerType: filters.providerTypes,
      serviceTypes: filters.serviceTypes,
      programType: filters.programTypes,
      searchTerm: this.searchTerm(),
      limit: THREE_PL_SERVICES_PAGE_SIZE,
      sort: this.sortValue()?.value,
    };
  }

  private setStateParams(): void {
    const {dateStart, dateEnd} = this.range();
    const filters = this.filters();
    const group = this.group();
    this.state.go(".", {
      from: dateStart,
      to: dateEnd,
      group: group === "TOTAL" ? undefined : group,
      depotIds: filters.depotIds,
      accountIds: filters.accountIds,
      branchIds: filters.branchIds,
      supplyAccountIds: filters.supplyAccountIds,
      areaIds: filters.serviceAreaIds,
      serviceTypes: filters.serviceTypes,
      programTypes: filters.programTypes,
      providerTypes: filters.providerTypes,
    }, STATE_URL_ONLY_OPTIONS);
  }

  private listenForPushes(): void {
    this.channel.pushes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((push) => {
        try {
          this.onPush(push);
        } catch (error) {
          this.log.error(error);
        }
      });
  }

  private onPush(push: ThreePLServicePush): void {
    if (push.action !== "created") {
      this.store.applyPush({action: push.action, entities: push.list});
      return;
    }
    const {dateStart, dateEnd} = this.range();
    const criteria = {searchTerm: this.searchTerm(), from: dateStart, to: dateEnd, filters: this.filters()};
    const group = this.group();
    const added = (push.list as ThreePLServiceDetails[])
      .filter((service) => matchesServicesFilters(service, criteria))
      .filter((service) => group === "TOTAL" || service.status === group);
    if (added.length) {
      this.store.prepend(added);
    }
  }

  private hasPermission(permission: string): boolean {
    return !!this.permissions.getPermission(permission);
  }
}

function isSameDay(from: number | undefined, to: number | undefined): boolean {
  return moment(from).format("MM/DD/YYYY") === moment(to).format("MM/DD/YYYY");
}

